import { useEffect, useState } from 'react';
import { Cartesian2, Cartesian3 } from 'cesium';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMapStore } from '../../store/mapStore';
import type { SensorSnapshot } from '../../store/mapStore';

const STATUS_CLASS: Record<string, string> = {
  ONLINE: 'bg-success/15 text-success border-success/40',
  DEGRADED: 'bg-warn/15 text-warn border-warn/40',
  OFFLINE: 'bg-alert/15 text-alert border-alert/40',
  ERROR: 'bg-alert/15 text-alert border-alert/40',
  MAINTENANCE: 'bg-iron-600/40 text-iron-200 border-iron-500',
};

interface HitTarget {
  sensor: SensorSnapshot;
  position: Cartesian2;
}

function samePosition(a: Cartesian2 | null, b: Cartesian2 | null): boolean {
  if (!a || !b) return a === b;
  return Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5;
}

function sameTargets(a: HitTarget[], b: HitTarget[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((target, index) => {
    const next = b[index];
    return target.sensor.id === next.sensor.id && samePosition(target.position, next.position);
  });
}

export default function SensorTooltip() {
  const viewer = useMapStore((s) => s.viewer);
  const sensor = useMapStore((s) => s.selectedSensor);
  const sensors = useMapStore((s) => s.sensorMarkers);
  const close = useMapStore((s) => s.setSelectedSensor);
  const selectSensor = useMapStore((s) => s.setSelectedSensor);
  const [screenPosition, setScreenPosition] = useState<Cartesian2 | null>(null);
  const [hitTargets, setHitTargets] = useState<HitTarget[]>([]);

  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) {
      setScreenPosition((current) => (current === null ? current : null));
      setHitTargets((current) => (current.length === 0 ? current : []));
      return;
    }

    const updatePosition = () => {
      if (viewer.isDestroyed()) return;

      const targets = sensors.flatMap((s) => {
        const worldPosition = Cartesian3.fromDegrees(
          s.longitude,
          s.latitude,
          s.altitudeMsl ?? 0,
        );
        const canvasPosition = viewer.scene.cartesianToCanvasCoordinates(worldPosition);
        return canvasPosition
          ? [{ sensor: s, position: Cartesian2.clone(canvasPosition) }]
          : [];
      });

      setHitTargets((current) => (sameTargets(current, targets) ? current : targets));

      if (!sensor) {
        setScreenPosition((current) => (current === null ? current : null));
        return;
      }

      const selectedTarget = targets.find((target) => target.sensor.id === sensor.id);
      const nextPosition = selectedTarget?.position ?? null;
      setScreenPosition((current) =>
        samePosition(current, nextPosition) ? current : nextPosition,
      );
    };

    updatePosition();
    viewer.scene.postRender.addEventListener(updatePosition);

    return () => {
      if (!viewer.isDestroyed()) {
        viewer.scene.postRender.removeEventListener(updatePosition);
      }
    };
  }, [sensor, sensors, viewer]);

  const statusClass = sensor
    ? STATUS_CLASS[sensor.status] ?? STATUS_CLASS.MAINTENANCE
    : STATUS_CLASS.MAINTENANCE;

  return (
    <>
      {hitTargets.map((target) => (
        <button
          key={target.sensor.id}
          type="button"
          aria-label={`Show sensor ${target.sensor.name}`}
          onClick={() => selectSensor(target.sensor)}
          className="absolute z-10 h-12 w-12 -translate-x-1/2 -translate-y-full cursor-pointer appearance-none border-0 bg-transparent p-0"
          style={{ left: target.position.x, top: target.position.y }}
        />
      ))}

      {sensor && screenPosition && (
        <div
          className="absolute z-20 w-64 -translate-x-1/2 -translate-y-[calc(100%+18px)] rounded-sm border border-iron-600 bg-iron-900/95 shadow-xl backdrop-blur-md"
          style={{ left: screenPosition.x, top: screenPosition.y }}
          role="dialog"
          aria-label={`Sensor ${sensor.name}`}
        >
          <div className="flex items-start justify-between gap-3 border-b border-iron-700 px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-[10px] font-mono font-semibold uppercase tracking-[0.18em] text-iron-400">
                Sensor
              </div>
              <div className="truncate text-sm font-semibold text-iron-100">
                {sensor.name}
              </div>
            </div>
            <button
              type="button"
              onClick={() => close(null)}
              className="mt-0.5 flex-shrink-0 rounded-sm p-1 text-iron-400 hover:bg-iron-700 hover:text-iron-100"
              title="Close"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-2 px-3 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] font-mono font-semibold uppercase tracking-[0.16em] text-iron-400">
                Status
              </span>
              <span className={`border px-1.5 py-0.5 text-[10px] font-mono font-semibold ${statusClass}`}>
                {sensor.status}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] font-mono font-semibold uppercase tracking-[0.16em] text-iron-400">
                Position
              </span>
              <span className="font-mono text-xs tabular-nums text-iron-100">
                {sensor.latitude.toFixed(4)}, {sensor.longitude.toFixed(4)}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
